import React from 'react';
import { BarChart3, CheckCircle2, Star } from 'lucide-react';
import { CONSENSUS, isUnanimousApply } from '../../utils/ipoReviews';

const Card = ({ label, aside, className = '', children }) => (
  <li className={`relative min-w-0 overflow-hidden bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5 ${className}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      {aside}
    </div>
    {children}
  </li>
);

const Chip = ({ className, children }) => (
  <span className={`shrink-0 px-2 py-0.5 rounded-md text-[11px] font-extrabold whitespace-nowrap ${className}`}>{children}</span>
);

const CONSENSUS_SEGMENTS = [
  { key: 'apply', label: 'Apply', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  { key: 'may', label: 'May', dot: 'bg-amber-500', bar: 'bg-amber-400' },
  { key: 'avoid', label: 'Avoid', dot: 'bg-rose-500', bar: 'bg-rose-500' },
];

export const ReviewStatCards = ({ stats }) => {
  const { total, sme, mainboard, counts, finalized, applyShare, topPick } = stats;
  const pickBrokers = topPick?.ratings?.length || 0;

  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <Card
        label="Total IPOs Tracked"
        aside={
          <span className="w-7 h-7 rounded-lg border border-slate-200 bg-slate-50 text-slate-400 flex items-center justify-center" aria-hidden="true">
            <BarChart3 className="w-3.5 h-3.5" />
          </span>
        }
      >
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <p className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{total} Issues</p>
          <Chip className="bg-teal-50 text-[#006666] border border-teal-100">
            {sme} SME / {mainboard} Main
          </Chip>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Across mainboard, BSE SME &amp; NSE Emerge platforms</p>
        <span className="absolute inset-x-0 bottom-0 h-1 bg-[#008080]" aria-hidden="true" />
      </Card>

      <Card
        label="Broker Consensus"
        aside={<Chip className="bg-emerald-50 text-emerald-700">{applyShare === null ? '—' : `${applyShare.toFixed(1)}% Apply`}</Chip>}
      >
        <ul className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-slate-600">
          {CONSENSUS_SEGMENTS.map((segment) => (
            <li key={segment.key} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${segment.dot}`} aria-hidden="true" />
              {counts[segment.key]} {segment.label}
            </li>
          ))}
        </ul>
        <div
          role="img"
          aria-label={`${counts.apply} apply, ${counts.may} may apply, ${counts.avoid} avoid and ${counts.awaited} awaiting review`}
          className="mt-3 flex h-2 rounded-full overflow-hidden bg-slate-200"
        >
          {total > 0 &&
            CONSENSUS_SEGMENTS.map((segment) => (
              <span key={segment.key} className={segment.bar} style={{ width: `${(counts[segment.key] / total) * 100}%` }} />
            ))}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-slate-400">
          <span>{counts.awaited} notes awaiting final review</span>
          <span>{finalized} finalized</span>
        </div>
      </Card>

      <Card
        label="Top Consensus Pick"
        aside={
          topPick && (
            <Chip className="inline-flex items-center gap-1 bg-amber-50 text-amber-700">
              <Star className="w-3 h-3 fill-current" aria-hidden="true" />
              CM {topPick.cmScore} / 100
            </Chip>
          )
        }
      >
        {topPick ? (
          <>
            <p className="mt-2 text-base sm:text-lg font-extrabold tracking-tight text-slate-900 truncate" title={topPick.company}>
              {topPick.company}
            </p>
            <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">
                {isUnanimousApply(topPick) ? 'Unanimous ' : ''}“{CONSENSUS[topPick.consensus].label}”
                {pickBrokers > 0 && ` (${pickBrokers} ${pickBrokers === 1 ? 'Broker' : 'Brokers'})`}
              </span>
            </p>
            <p className="mt-1.5 text-[11px] text-slate-400 truncate">{topPick.sector}</p>
          </>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No apply-rated issue yet</p>
        )}
      </Card>

      <Card
        label="Research Coverage"
        aside={
          stats.coverage.brokerCount > 0 && (
            <Chip className="bg-emerald-50 text-emerald-700">
              {stats.coverage.brokerCount} {stats.coverage.brokerCount === 1 ? 'broker' : 'brokers'}
            </Chip>
          )
        }
      >
        <p className="mt-2 flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl sm:text-[26px] leading-tight font-extrabold tracking-tight text-slate-900">{stats.coverage.calls}</span>
          <span className="text-[11px] text-slate-400">published broker {stats.coverage.calls === 1 ? 'call' : 'calls'}</span>
        </p>
        <p className="mt-1.5 text-[11px] text-slate-400 leading-relaxed">
          Across {stats.finalized} {stats.finalized === 1 ? 'issue' : 'issues'} with a finalised review
        </p>
      </Card>
    </ul>
  );
};

export default ReviewStatCards;
